/**
 * Pre-React startup guard for the native iOS/Android shells.
 *
 * Apple rejected Build 8 under Guideline 2.1(a) with a blank white screen on
 * iPad. Anything that throws before React mounts (a failed bundle request, a
 * native plugin that is not available, a webview that cannot reach the host)
 * left the WebView showing nothing at all. This script runs as the first thing
 * in the document and guarantees one of two outcomes: the real app, or a
 * branded, recoverable "Unable to load Inquira" screen with a Retry button.
 *
 * It never swallows an error: every failure is kept in `window.__inquiraBoot`
 * and re-logged to the console (and surfaced on the fallback screen), so the
 * real reason is still visible in Safari/Chrome remote inspectors and in the
 * in-app diagnostics screen.
 *
 * The copy here is intentionally not translated: i18n is part of the React
 * bundle, which by definition has not loaded when this screen is shown.
 */
export const BOOT_FALLBACK_SCRIPT = `
(function () {
  if (window.__inquiraBoot) return;
  // The full-screen fallback is for the native iOS/Android shells only. On the
  // regular website the page is server-rendered and fully readable/clickable
  // before React hydrates, so an overlay here would cover a working page.
  var isNative =
    typeof window.Capacitor !== "undefined" ||
    window.location.protocol === "capacitor:" ||
    window.location.protocol === "ionic:";
  var boot = { mounted: false, logs: [], shown: false, native: isNative };
  window.__inquiraBoot = boot;

  function log(kind, message) {
    var line = new Date().toISOString() + " [" + kind + "] " + String(message || "");
    boot.logs.push(line);
    if (boot.logs.length > 30) boot.logs.shift();
    try { console.error("[inquira.boot] " + line); } catch (e) {}
    var pre = document.getElementById("inq-boot-log");
    if (pre) pre.textContent = boot.logs.join("\\n");
  }
  boot.log = log;

  function show(reason) {
    if (!boot.native || boot.mounted || boot.shown) return;
    boot.shown = true;
    log("fallback", reason);
    if (!document.body) {
      document.addEventListener("DOMContentLoaded", function () { render(); }, { once: true });
      return;
    }
    render();
  }

  function render() {
    if (document.getElementById("inq-boot-fallback")) return;
    var el = document.createElement("div");
    el.id = "inq-boot-fallback";
    el.setAttribute("role", "alert");
    el.style.cssText = "position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;padding:32px 24px;text-align:center;background:#ffffff;color:#0f172a;font:16px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;-webkit-user-select:none;user-select:none";
    el.innerHTML =
      '<img src="/apple-touch-icon.png" alt="Inquira" width="88" height="88" style="width:88px;height:88px;object-fit:contain" />' +
      '<h1 style="margin:0;font-size:22px;font-weight:800">Unable to load Inquira</h1>' +
      '<p style="margin:0;max-width:28rem;font-size:15px;color:#475569">Inquira could not finish starting up. Check your internet connection and try again.</p>' +
      '<button id="inq-boot-retry" type="button" style="appearance:none;border:0;border-radius:12px;background:#0f172a;color:#fff;font-size:16px;font-weight:600;padding:14px 28px;min-height:48px">Retry</button>' +
      '<details style="max-width:32rem;width:100%;text-align:left"><summary style="font-size:13px;color:#64748b">Technical details</summary>' +
      '<pre id="inq-boot-log" style="max-height:180px;overflow:auto;white-space:pre-wrap;word-break:break-word;font-size:11px;color:#475569;background:#f1f5f9;border-radius:8px;padding:10px"></pre></details>';
    document.body.appendChild(el);
    var pre = document.getElementById("inq-boot-log");
    if (pre) pre.textContent = boot.logs.join("\\n");
    var retry = document.getElementById("inq-boot-retry");
    if (retry) retry.addEventListener("click", function () { window.location.reload(); });
  }
  boot.show = show;

  boot.clear = function () {
    boot.mounted = true;
    var el = document.getElementById("inq-boot-fallback");
    if (el && el.parentNode) el.parentNode.removeChild(el);
  };

  window.addEventListener("error", function (event) {
    log("error", (event && (event.message || (event.error && event.error.message))) || "unknown");
    if (boot.native && !boot.mounted) show("uncaught error before mount");
  });
  window.addEventListener("unhandledrejection", function (event) {
    var r = event && event.reason;
    log("rejection", (r && (r.message || r)) || "unknown");
  });

  // Long enough for a cold native launch on a slow connection, short enough
  // that a reviewer never sits in front of a blank screen.
  if (isNative) {
    setTimeout(function () { show("app did not mount within 12s"); }, 12000);
  }
})();
`;
